import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { handleUnexpectedError } from '@/app/errors';
import { useCurrentUser } from '@/app/hooks/use-current-user';
import { updateProfile } from '@/app/network/firefly-api';
import { Button, ButtonGroup } from '@/common/components/button';
import { Heading } from '@/common/components/heading';
import { EmptyState, EmptyStateImage, EmptyStateMessage } from '@/shared/components/empty-states';
import { getCurrentUserQueryOptions } from '@/shared/user/queries';

export const Route = createFileRoute('/wizard/memberships/$member-number/newsletter')({
  component: NewsLetterPage
});

function NewsLetterPage() {
  const { membership } = Route.useRouteContext();
  const user = useCurrentUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutateAsync } = useMutation({
    mutationFn: async (value: boolean) => {
      await updateProfile(
        user.id,
        user.first_name,
        user.last_name,
        navigator.language,
        value
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getCurrentUserQueryOptions()['queryKey'] });
      await navigate({ to: '/wizard/memberships/$member-number/go-paperless', params: { 'member-number': membership.member_number.toString() } });
    },
    onError: error => handleUnexpectedError(error)
  });

  const handleNewsletterPreference = async (digital: boolean) => {
    await mutateAsync(digital);
  };

  return (
    <>
      <Heading>Newsletter</Heading>

      <EmptyState>
        <EmptyStateImage src="/img/wizard_newsletter.svg" alt="An illustration of an envelope containing a letter with an at symbol on it."/>
        <EmptyStateMessage>
          Would you like to receive Talquin’s newsletter, <em>The Current</em>, digitally?
        </EmptyStateMessage>
        <ButtonGroup vertical className="min-w-52">
          <Button onClick={() => handleNewsletterPreference(true)}>Subscribe Digitally</Button>
          <Button onClick={() => handleNewsletterPreference(false)} outline>No Thanks</Button>
        </ButtonGroup>
      </EmptyState>
    </>
  );
}
